"use client";
import { FormikSubmit } from "@/components/Button";
import { useCaptcha } from "@/components/CaptchaComponent";
import ErrorField from "@/components/ErrorField";
import { FieldInput } from "@/components/Input";
import InputCaptcha from "@/components/InputCaptcha";
import TogglePassword, { useTogglePassword } from "@/components/TogglePassword";
import { handleApiErr, useAPI } from "@/lib/api";
import { assetPrefix } from "@/vars/assetUrl";
import { Form, Formik } from "formik";
import { LockOpenIcon } from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Trans } from "react-i18next";

const RequestProtectedPage = () => {
  const [data, setData] = useState<any>(null);

  const onReceive = (e) => {
    const _data = e.data;
    if (_data.from === "microsite_protected" && _data.action === "SYNC") {
      setData(_data.data);
    }
  };

  useEffect(() => {
    window?.opener?.postMessage(
      { from: "microsite_protected", action: "READY" },
      "*"
    );
    window.addEventListener("message", onReceive);
    return () => {
      window.removeEventListener("message", onReceive);
    };
  }, []);

  const onSuccess = (data) => {
    window?.opener?.postMessage(
      {
        from: "microsite_protected",
        action: "SYNC",
        data,
      },
      "*"
    );

    setTimeout(() => {
      window.close();
    }, 500);
  };

  return (
    <div>
      {data !== null ? (
        <div className="pt-10">
          <div className="top-0 left-0 z-50 fixed bg-white p-3 w-full">
            <img
              src={`${assetPrefix}images/sid-neu-logo-dark.svg`}
              alt="Logo"
              className="h-8"
            />
          </div>
          {/* <pre>{JSON.stringify(data, null, 2)}</pre> */}
          <RequestComponent
            micrositeId={data.micrositeId}
            componentId={data.componentId}
            revealedInfo={data.revealedInfo}
            message={data.message}
            onSuccess={onSuccess}
          />
        </div>
      ) : (
        <Fragment />
      )}
    </div>
  );
};

type RequestComponentProp = {
  message?: string;
  micrositeId: string;
  componentId: string;
  revealedInfo: boolean;
  onSuccess: (data: any) => void;
};

const RequestComponent = ({
  message = "",
  micrositeId,
  componentId,
  revealedInfo,
  onSuccess,
}: RequestComponentProp) => {
  const api = useAPI();
  const captchaProp = useCaptcha();
  const togglePass = useTogglePassword();
  const onSubmit = async (values, { setErrors, setFieldValue }) => {
    if (values.password === "") {
      return;
    }

    if (revealedInfo && values.captcha === "") {
      return;
    }

    try {
      if (revealedInfo) {
        const data = await api.post(`/microsite/cv`, {
          micrositeId: micrositeId,
          componentId: componentId,
          captcha: values.captcha,
        });
        onSuccess({ ...data.data, componentId, revealedInfo });
      } else {
        const data = await api.post(`/microsite/protected`, {
          microsite_id: micrositeId,
          component_id: componentId,
          password: values.password,
        });
        toast.success("Password is correct!");
        onSuccess({ ...data.data.data, componentId, revealedInfo });
      }
    } catch (e) {
      captchaProp.reset();
      setFieldValue("captcha", "", false);
      handleApiErr(e, {
        toast,
        t: (v) => v,
        setErrors: null,
        onError: (msg) => {
          if (msg === "password_mismatch") {
            setErrors({
              password: "Invalid password",
            });
            return true;
          }
          return false;
        },
      });
    }
  };

  if (revealedInfo) {
    return (
      <Formik initialValues={{ captcha: "" }} onSubmit={onSubmit}>
        <Form className="p-8 text-gray-800">
          <div className="text-center">
            <h1 className="mb-2 font-montserrat font-bold text-xl font">
              <Trans i18nKey="solve_captcha">Solve Captcha</Trans>
            </h1>
          </div>
          <p className="text-center">
            <Trans i18nKey="complete_captcha">
              Please complete the captcha below
            </Trans>
          </p>
          <div className="flex justify-center my-6">
            <InputCaptcha prop={captchaProp} action="reveal_cv_info" />
          </div>

          <FormikSubmit type="submit" variant="blue" className="w-full">
            Continue
          </FormikSubmit>
        </Form>
      </Formik>
    );
  }

  return (
    <Formik initialValues={{ password: "", captcha: "" }} onSubmit={onSubmit}>
      <Form className="p-3 pt-8 text-gray-800">
        <div className="bg-muted/50 mb-4 p-3 border rounded text-sm">
          {message || "Enter password to continue..."}
        </div>
        <div className="relative mb-3">
          <div className="relative">
            <FieldInput
              type={togglePass.isOpen ? "text" : "password"}
              placeholder="Type password"
              name="password"
              inputSize="lg"
              className="w-full"
              required
            />
            <TogglePassword {...togglePass} />
          </div>
          <ErrorField name="password" />
        </div>
        {/* <div className="flex justify-center my-6">
          <InputCaptcha prop={captchaProp} action="protected_component" />
        </div> */}

        <div className="mt-4 mb-3">
          <FormikSubmit type="submit" variant="blue" className="w-full">
            <LockOpenIcon />
            Open
          </FormikSubmit>
        </div>
      </Form>
    </Formik>
  );
};

export default RequestProtectedPage;
